/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  FC,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DataTable,
  DataTableFilterMeta,
  DataTableValueArray,
  DataTableSelectionMultipleChangeEvent,
} from 'primereact/datatable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';

import {
  Column,
  ColumnFilterApplyTemplateOptions,
  ColumnFilterClearTemplateOptions,
  ColumnFilterElementTemplateOptions,
  ColumnProps,
} from 'primereact/column';
import classNames from 'classnames';
import { InputText } from 'primereact/inputtext';
import { useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { TableData } from './types';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import styles from './Table.module.scss';
import Button from '../Button/Button';

import './Table.scss';

type TableProps = {
  columns: ColumnProps[];
  data?: TableData;
  isLoading?: boolean;
  headerColumnGroup?: ReactNode;
  emptyMessage?: ReactNode;
  className?: string;
  filterDisplay?: 'menu' | 'row';
  filters?: DataTableFilterMeta;
  paginator?: boolean;
  selectableColumns?: boolean;
  selectionId?: string;
  onSelectionChange?: (e: DataTableSelectionMultipleChangeEvent<any>) => void;
  selectedItems?: DataTableValueArray | undefined;
  darkHeader?: boolean;
  footer?: ReactNode;
  [key: string]: any;
  rows?: number;
  forceRefresh?: boolean;
  autoRowsToFillViewport?: boolean;
};

export const filterClearTemplate = (
  options: ColumnFilterClearTemplateOptions
) => {
  return (
    <Button
      secondary
      onClick={options.filterClearCallback}
      className={styles.clearButton}
    >
      <FontAwesomeIcon icon={faTimes} />
    </Button>
  );
};

const FilterApplyButton: React.FC<{ onApply: () => void }> = ({ onApply }) => {
  const { t } = useTranslation('table');
  return (
    <Button onClick={onApply} className={styles.applyButton}>
      {t('apply')}
    </Button>
  );
};

export const filterApplyTemplate = (
  options: ColumnFilterApplyTemplateOptions
) => {
  return <FilterApplyButton onApply={options.filterApplyCallback} />;
};

export const inputTextTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <InputText
      type="text"
      value={options.value ?? ''}
      onChange={(e) => {
        options.filterApplyCallback();
        return options.filterCallback(e.target.value);
      }}
      autoFocus
    />
  );
};

const Table: FC<TableProps> = ({
  columns: initialColumns,
  data,
  isLoading,
  headerColumnGroup,
  emptyMessage,
  className,
  filterDisplay,
  filters,
  paginator,
  selectionId,
  onSelectionChange,
  selectedItems,
  darkHeader,
  footer,
  rows,
  forceRefresh,
  autoRowsToFillViewport,
  ...props
}) => {
  const { t } = useTranslation('table');
  const location = useLocation();
  const localStorageKey = `table-columns-${location.pathname}-${selectionId ?? ''}`;

  const [columns, setColumns] = useState<ColumnProps[]>();
  const [calculatedRows, setCalculatedRows] = useState<number>(rows ?? 10);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateCalculatedRows = useCallback(() => {
    if (!autoRowsToFillViewport || !paginator || !containerRef.current) {
      return;
    }

    const minimumRows = rows ?? 10;
    const containerTop = containerRef.current.getBoundingClientRect().top;
    const firstRow = containerRef.current.querySelector(
      '.p-datatable-tbody > tr'
    ) as HTMLElement | null;
    const tableHeader = containerRef.current.querySelector(
      '.p-datatable-thead'
    ) as HTMLElement | null;
    const paginatorElement = containerRef.current.querySelector(
      '.p-paginator'
    ) as HTMLElement | null;

    const rowHeight = firstRow?.getBoundingClientRect().height || 44;
    const headerHeight = tableHeader?.getBoundingClientRect().height || 44;
    const paginatorHeight =
      paginatorElement?.getBoundingClientRect().height || 52;
    const viewportAvailableHeight = window.innerHeight - containerTop - 16;
    const rowsThatFit = Math.floor(
      (viewportAvailableHeight - headerHeight - paginatorHeight - 12) /
        rowHeight
    );

    if (Number.isFinite(rowsThatFit)) {
      setCalculatedRows(Math.max(minimumRows, rowsThatFit));
    }
  }, [autoRowsToFillViewport, paginator, rows]);

  useEffect(() => {
    const savedColumns = localStorage.getItem(localStorageKey);
    if (savedColumns) {
      const parsedColumns: ColumnProps[] = JSON.parse(savedColumns);
      if (parsedColumns && Array.isArray(parsedColumns)) {
        const reorderedColumns = parsedColumns.map(
          (parsedColumn) =>
            initialColumns.find((col) => col.field === parsedColumn.field) ||
            parsedColumn
        );

        setColumns(reorderedColumns);
      }
    }
  }, [location.pathname, initialColumns, localStorageKey]);

  useEffect(() => {
    if (!autoRowsToFillViewport || !paginator) {
      return undefined;
    }

    updateCalculatedRows();
    const onResize = () => updateCalculatedRows();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [autoRowsToFillViewport, paginator, updateCalculatedRows, data]);

  const onColReorder = (event: any) => {
    const reorderedColumns = event.columns.map((column: any) => column.props);

    localStorage.setItem(localStorageKey, JSON.stringify(reorderedColumns));
    setColumns(reorderedColumns);

    if (forceRefresh) {
      window.location.reload();
    }
  };

  const renderColumns = useMemo(() => {
    const columnsToRender = columns ?? initialColumns;
    return columnsToRender.map((col) => (
      <Column
        showFilterMatchModes={false}
        filterApply={filterDisplay === 'menu' && filterApplyTemplate}
        filterClear={filterDisplay === 'menu' && filterClearTemplate}
        key={col.field}
        pt={{
          sortIcon: {
            className: darkHeader
              ? 'sort-icon-custom-dark-header'
              : 'sort-icon-custom',
          },
        }}
        filterElement={col.filterElement ?? inputTextTemplate}
        frozen={
          col.selectionMode === 'single' || col.selectionMode === 'multiple'
        }
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...col}
      />
    ));
  }, [columns, initialColumns, filterDisplay, darkHeader]);

  const table = (
    <DataTable
      filterIcon={<FontAwesomeIcon icon={faFilter} color="#223466" />}
      value={data}
      scrollable
      scrollHeight="flex"
      className={classNames(
        styles.table,
        className,
        darkHeader && 'darkHeader'
      )}
      headerColumnGroup={headerColumnGroup}
      emptyMessage={emptyMessage || t('noData')}
      filterDisplay={filterDisplay}
      filters={filters}
      loading={isLoading}
      loadingIcon={<LoadingSpinner />}
      paginator={paginator}
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
      currentPageReportTemplate={`${t('showing')} {first} - {last} ${t('of')} {totalRecords} ${t('items')}`}
      rows={autoRowsToFillViewport ? calculatedRows : rows ?? 10}
      selectionMode={null}
      selection={selectedItems || []}
      onSelectionChange={(e) => {
        if (onSelectionChange) {
          onSelectionChange(e);
        }
      }}
      dataKey={selectionId}
      autoFocus
      globalFilterFields={[]}
      footer={footer}
      reorderableColumns={!headerColumnGroup}
      onColReorder={onColReorder}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {renderColumns}
    </DataTable>
  );

  if (autoRowsToFillViewport) {
    return <div ref={containerRef}>{table}</div>;
  }

  return table;
};

export default memo(Table);
